import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { Link } from "react-router-dom";
import { ChevronRight, PersonStanding, ShieldCheck, Activity, AlertCircle, CheckCircle2, Clock, Target } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

export default function SpinalDecompression() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="DRX9000 Spinal Decompression | Woods Spine & Wellness | Longview TX"
        description="Get non-surgical relief for back and neck pain, herniated discs, and sciatica with DRX9000 Spinal Decompression in Longview, TX."
        ogImage="https://www.excitemedical.com/wp-content/uploads/2024/11/drx9000-lumbar-numbered-graphic.png"
        canonical="https://woodsspine.com/longview-spinal-decompression"
      />
      <Navbar />
      <main className="flex-grow pt-24 pb-16">
        <section className="container mx-auto px-4 mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center rounded-full border bg-secondary/50 px-3 py-1 text-sm font-medium text-primary mb-2">
                <PersonStanding className="mr-2 h-4 w-4 text-accent" />
                Non-Surgical Relief
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight">
                DRX9000® <br />
                <span className="text-accent">Spinal Decompression</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                The world's most advanced non-surgical treatment for chronic back and neck pain. Say farewell to invasive procedures and reliance on pain medication.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button size="lg" className="bg-accent text-white hover:bg-accent/90" asChild>
                  <Link to="/appointment-scheduling">
                    Book Your Consultation <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-primary/5 rounded-3xl transform rotate-3"></div>
              <div className="bg-white p-8 rounded-3xl shadow-xl relative z-10 border border-border flex items-center justify-center min-h-[400px]">
                <img 
                  src="https://wsrv.nl/?url=https://www.excitemedical.com/wp-content/uploads/2024/11/drx9000-lumbar-numbered-graphic.png&w=1280&output=webp&q=80" 
                  alt="DRX9000 Spinal Decompression Device" 
                  className="max-w-full h-auto object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        {/* WHAT IT IS */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">What is DRX9000® Spinal Decompression?</h2>
            <div className="prose prose-lg text-muted-foreground">
              <p>
                The DRX9000® True Non-Surgical Spinal Decompression® system is a state-of-the-art medical technology designed to provide highly effective, non-invasive relief from chronic back and neck pain. For decades, patients suffering from severe disc issues, herniations, and sciatica were often told that risky spinal surgery, repeated cortisone injections, or a lifetime of pain management were their only options. The DRX9000 changes the landscape of spinal care by offering a conservative, clinically proven alternative that addresses the root cause of the pain rather than merely masking the symptoms.
              </p>
              <p>
                This advanced therapy works by gently and precisely stretching the spine to relieve pressure on compressed discs and pinched nerves. By creating a vacuum effect—scientifically known as negative intra-discal pressure—within the intervertebral discs, the DRX9000 encourages herniated or bulging disc material to retract back into its proper anatomical position. This process not only relieves the immediate pressure on the surrounding sensitive nerves but also promotes a natural healing environment by drawing vital fluids and nutrients back into the damaged disc.
              </p>
              <p>
                What truly sets the DRX9000 apart from traditional, outdated traction devices is its sophisticated computerized servo-motor technology. This intelligent system constantly monitors your body's resistance thousands of times per second and adjusts the pulling force accordingly. This ensures that your spinal muscles remain completely relaxed throughout the entire treatment, preventing the painful muscle spasms that often make traditional traction uncomfortable and ineffective. The result is true, targeted decompression of the spinal discs in a comfortable and profoundly relaxing environment.
              </p>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">How It Works</h2>
            <p className="text-lg text-muted-foreground mb-8">
              The DRX9000® system works on injuries caused by nerve compression by applying an equal, calculated amount of pressure on the vertebral columns. Here is the step-by-step process of how decompression facilitates long-term healing:
            </p>
            
            <div className="grid gap-6">
              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6 flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <span className="font-bold text-xl">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Targeted Stretching & Isolation</h3>
                    <p className="text-muted-foreground">Using a customized, highly secure harness system, the DRX9000 gently isolates and targets the specific spinal segment that is causing your pain. The computerized system slowly applies a precise, logarithmic pulling force, elongating the spine without triggering the body's natural muscle guarding reflex.</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6 flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <span className="font-bold text-xl">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Creating Negative Pressure (The Vacuum Effect)</h3>
                    <p className="text-muted-foreground">As the spine stretches, a powerful vacuum—or negative pressure—is created within the targeted disc. This vacuum acts much like a suction cup, gently drawing the bulging or herniated gel-like center of the disc (the nucleus pulposus) back toward its normal, centralized position away from the nerve roots.</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6 flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <span className="font-bold text-xl">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Vital Nutrient Exchange & Rehydration</h3>
                    <p className="text-muted-foreground">Spinal discs have a very poor blood supply, making them slow to heal. The negative pressure created by the DRX9000 acts as a pump, drawing vital fluids, oxygen, and nutrient-rich blood from the surrounding tissues back into the degenerated or torn disc. This profound rehydration is essential for the disc to naturally heal and repair itself over time.</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6 flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <span className="font-bold text-xl">4</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Alleviating Nerve Compression & Pain</h3>
                    <p className="text-muted-foreground">As the disc retracts and rehydrates, the physical pressure and chemical irritation on the pinched spinal nerves are finally lifted. This significantly reduces the debilitating pain, numbness, burning, and tingling sensations that often radiate down the arms or legs, restoring your mobility and quality of life.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* WHO IT'S FOR */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-10 text-center">Who is the DRX9000 For?</h2>
            
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold text-primary mb-6 flex items-center">
                  <CheckCircle2 className="h-6 w-6 text-accent mr-2" /> Ideal Candidates
                </h3>
                <p className="text-muted-foreground mb-6">
                  The DRX9000 is an excellent, life-changing option for patients who have been suffering from chronic back or neck pain and have not found lasting relief through traditional chiropractic care, physical therapy, or medication. It effectively treats:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Herniated, bulging, or slipped discs</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Degenerative Disc Disease (DDD)</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Sciatica and radiating leg pain or numbness</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Facet Syndrome and generalized lower back pain</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Spinal Stenosis (in specific, evaluated cases)</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Failed Back Surgery Syndrome (depending on the type of prior surgery)</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-primary mb-6 flex items-center">
                  <AlertCircle className="h-6 w-6 text-destructive mr-2" /> Contraindications
                </h3>
                <p className="text-muted-foreground mb-6">
                  While spinal decompression is highly safe and non-invasive, it is not suitable for everyone. You are likely NOT a candidate for DRX9000 therapy if you currently have:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Prior spinal fusion surgery with retained hardware (screws, plates, rods)</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Severe osteoporosis or significant bone density loss</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Pregnancy</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Spinal fractures, recent spinal trauma, or unstable spondylolisthesis</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Spinal tumors, infections, or active cancer in the spine</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Abdominal aortic aneurysm</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT TO EXPECT */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">What to Expect During Your Treatment</h2>
            <div className="prose prose-lg text-muted-foreground mb-8">
              <p>
                Many patients find DRX9000 sessions to be incredibly relaxing and therapeutic—in fact, it is very common for patients to fall asleep during their treatment! Here is a breakdown of what you can expect when you visit us for decompression therapy:
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-8">
              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Clock className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Duration</h3>
                  <p className="text-muted-foreground">A typical DRX9000 session lasts between 30 and 45 minutes on the decompression table. We often follow the decompression with targeted cold therapy or other complementary modalities to reduce localized inflammation and maximize the benefits of the stretch.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Activity className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">What You'll Feel</h3>
                  <p className="text-muted-foreground">You will be fitted with a comfortable, secure harness and asked to lie down on the specialized table. As the computerized machine begins, you will feel a slow, gentle, and highly controlled stretching in your lower back or neck. It should not be painful; most patients describe it as a deeply relieving and satisfying stretch.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Target className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Recommended Sessions</h3>
                  <p className="text-muted-foreground">Healing a damaged, avascular disc takes time and consistency. A standard DRX9000 protocol typically requires 20 to 24 sessions spaced out over 6 to 8 weeks to achieve optimal, long-lasting disc rehydration, structural repair, and permanent pain relief.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <ShieldCheck className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Recovery & Downtime</h3>
                  <p className="text-muted-foreground">There is absolutely no downtime required. You can resume light daily activities, work, and driving immediately after your session. However, we do strictly advise against heavy lifting, twisting, or strenuous exercise during your treatment protocol to allow the fragile discs the proper environment to heal.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* LOCAL RELEVANCE */}
        <section className="py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Advanced Spine Care in Longview, TX</h2>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">
              At Woods Spine & Wellness, we are incredibly proud to be one of the premier clinics in the Longview, TX and greater East Texas area to offer the authentic DRX9000® True Non-Surgical Spinal Decompression® system. We deeply understand how debilitating severe back pain and sciatica can be for our local community members, affecting your ability to work, enjoy family time, and stay active. Our mission is to provide our neighbors with a safe, proven, and highly effective non-surgical alternative to invasive spine procedures so you can get back to living life on your terms right here in East Texas.
            </p>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-8">
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Is DRX9000 Spinal Decompression safe?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Yes, the DRX9000 is an incredibly safe, FDA-cleared medical device that has been used globally for decades. Because it utilizes advanced computer feedback to constantly monitor your muscles, it actively prevents the muscle spasms that can make older traction devices unsafe or painful. It stands as a completely non-invasive, low-risk alternative to spine surgery or epidural injections.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">How long until I see results?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Every patient heals at their own unique pace depending on the severity of their disc degeneration. Some patients feel significant relief within the first few sessions, while others may not notice major changes until the second or third week of the protocol. It is crucial to complete the entire prescribed treatment plan, as the ultimate goal is long-term structural disc repair, not just temporary symptom relief.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Is Spinal Decompression covered by insurance?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Most major medical insurance plans and Medicare do not cover the cost of computerized spinal decompression therapy, viewing it as an elective procedure. However, we are deeply committed to making this life-changing treatment affordable for our community. We offer free initial consultations to determine if you are a candidate, and we provide highly flexible financing options and payment plans for our patients.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">What is the success rate of the DRX9000?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Clinical studies and extensive, long-term patient data have shown that the DRX9000 has a remarkably high success rate. It is often cited as being effective for 86% to 90% of patients suffering from herniated discs, bulging discs, and sciatica. These high success rates are achieved when patients strictly follow the full treatment protocol and our doctors' recommendations.
                </p>
              </div>
              <div className="pb-2">
                <h3 className="text-xl font-bold text-primary mb-3">Can I have this treatment if I've already had back surgery?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  It depends entirely on the specific type of surgery you have undergone. If you have had spinal fusion surgery with hardware (such as screws, rods, or plates) left in your spine, you are generally not a candidate for decompression in that specific area. However, if you have had a microdiscectomy, laminectomy, or a surgery without fusion, you may still be an excellent candidate. Our doctors will carefully review your surgical history and imaging during your consultation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-secondary/30 text-center border-t border-border">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Avoid Surgery and Stop the Pain</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Find out if you are a candidate for DRX9000 Spinal Decompression. Schedule your comprehensive consultation with our doctors today.
            </p>
            <Button size="lg" className="bg-accent text-white hover:bg-accent/90 border-0" asChild>
              <Link to="/appointment-scheduling">
                Book Your Consultation Now <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
